export type CheckoutDatacenter = {
  id: number
  name: string
  region_name?: string | null
  image?: string | null
  deployable?: boolean
}

/** Turn the datacenter fields already in the API into customer-facing labels. */
export function useDatacenterDisplay() {
  const { locale } = useI18n()

  /** Existing flag filenames are ISO country codes (for example br.png). */
  function countryCode(datacenter: CheckoutDatacenter): string | null {
    const match = datacenter.image?.match(
      /(?:^|\/)([a-z]{2})(?:\.[a-z0-9]+)?(?:\?.*)?$/i
    )
    return match?.[1]?.toUpperCase() ?? null
  }

  function countryName(datacenter: CheckoutDatacenter): string {
    const code = countryCode(datacenter)
    if (code) {
      try {
        return (
          new Intl.DisplayNames([locale.value], { type: 'region' }).of(code) ??
          datacenter.region_name ??
          code
        )
      } catch {
        // Older webviews may not support Intl.DisplayNames.
      }
    }
    return datacenter.region_name ?? ''
  }

  function countryFlag(datacenter: CheckoutDatacenter): string {
    const code = countryCode(datacenter)
    if (!code) return '🌐'
    return [...code]
      .map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
      .join('')
  }

  function locationLabel(datacenter: CheckoutDatacenter | null): string {
    if (!datacenter) return ''
    const country = countryName(datacenter)
    return country ? `${datacenter.name}, ${country}` : datacenter.name
  }

  return { countryCode, countryName, countryFlag, locationLabel }
}
